import React, { useContext, useMemo, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Gallery } from '../../../editor/features/build/components/widgets/Gallery/Gallery';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import WidgetModel, {
  GalleryWidgetConfig,
  WidgetBase,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { ActivePathSelection } from '../common/ActivePathSelection/ActivePathSelection';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './GalleryWidget.pcss';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { useResolutionInfo } from '@mm/hooks';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  disabledButtonTypes?: ButtonType[];
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  platforms?: PlatformType[];
  showAttributePicker?: boolean;
  smsOnly?: boolean;
  usedInSlideout?: boolean;
  widgetsToExclude?: WidgetTypes[];
} & WidgetContainerRefProp;

export const GalleryWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    disabledButtonTypes,
    isSmsRelated,
    forInstagram,
    smsOnly,
    platforms,
    showAttributePicker = true,
    usedInSlideout,
    widgetsToExclude,
  } = props;

  const chatbotId = useSelector(activeChatbotIdSelector);
  const [unsavedEntryButton, setUnsavedEntryButton] = useState<ButtonModel>();

  const { getActiveButtonFromWidgetButtons } = useContext(PathGraphContext);

  const activeButton = useMemo(() => {
    const allWidgetButtonsArray =
      widget.config.entries?.map(e => e.buttons) || ([[]] as ButtonModel[][]);
    const allWidgetButtons = ([] as ButtonModel[]).concat(
      ...allWidgetButtonsArray,
    );

    return (
      unsavedEntryButton || getActiveButtonFromWidgetButtons?.(allWidgetButtons)
    );
  }, [unsavedEntryButton, getActiveButtonFromWidgetButtons, widget]);

  const { isDesktop } = useResolutionInfo();

  return (
    <div className="gallery-widget">
      <div className="gallery-widget__main-area-wrapper">
        <FacebookPageIcon />

        <div className="gallery-widget__right-area">
          <div className="gallery-widget__right-top-area">
            <Gallery
              {...props}
              widget={
                widget as WidgetBase<GalleryWidgetConfig> & Partial<WidgetModel>
              }
              showAttributePicker={showAttributePicker}
              setUnsavedEntryButton={setUnsavedEntryButton}
              disabledButtonTypes={disabledButtonTypes}
              usedInSlideout={usedInSlideout}
              forInstagram={forInstagram}
            />

            <WidgetControls
              widget={widget}
              draggableProvided={draggableProvided}
              platforms={platforms}
            />

            {isSmsRelated && !widget.supportedByPlatforms.sms && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError isError={smsOnly} type="sms" />
              </ErrorPortal>
            )}
            {forInstagram && !widget.supportedByPlatforms.instagram && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError isError type="instagram" />
              </ErrorPortal>
            )}
          </div>
        </div>
      </div>

      {activeButton && (
        <ActivePathSelection
          widgetsToExclude={widgetsToExclude}
          chatbotId={chatbotId}
          widget={widget}
          activeButton={activeButton}
          closeExtraActions={() => setUnsavedEntryButton(undefined)}
          platforms={platforms}
          usedInSlideout={usedInSlideout}
        />
      )}
    </div>
  );
};
